import React, { useState } from "react";
import { Link } from "react-router-dom";
// import { useCart } from "../components/CartContext"; // <-- We no longer need CartContext!
import { loadStripe } from "@stripe/stripe-js";

// Load your key from .env.local or .env.production
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_replace_me"
);

// Build absolute URLs for product images (Stripe requires absolute)
const toAbsoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const needsSlash = u && u.charAt(0) !== "/";
  return `${origin}${needsSlash ? "/" : ""}${u}`;
};

// Decide which backend URL to use
const getCheckoutUrl = () => {
  if (typeof window === "undefined") return "";
  const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return isLocal
    ? "http://127.0.0.1:5002/unlimited-levels-d5bed/us-central1/createCheckoutSessionV2" // emulator
    : "/api/createCheckout";
};

// Component now accepts props from App.js
export default function CartPage({
  cartItems,
  onRemoveItem,
  onUpdateQuantity,
  onClearCart,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const total = cartItems.reduce(
    (sum, i) => sum + (i.price || 0) * (i.quantity || 1),
    0
  );

  const handleCheckout = async () => {
    setErrorMsg("");
    if (!cartItems.length) {
      setErrorMsg("Your cart is empty.");
      return;
    }
    setSubmitting(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to initialize.");

      const items = cartItems.map((i) => ({
        name: i.name || "Item",
        quantity: i.quantity || 1,
        amount: Math.round((i.price || 0 + 0) * 100), // cents
        currency: "usd",
        image: toAbsoluteUrl(i.imageUrl || i.img || i.image),
        metadata: { sku: i.id || "", variant: i.variant || "" },
      }));

      const res = await fetch(getCheckoutUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items,
          success_url: `${window.location.origin}/thanks`,
          cancel_url: `${window.location.origin}/cart`,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Checkout error:", res.status, text);
        throw new Error(`Checkout failed (${res.status}).`);
      }

      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url; // Stripe Hosted Checkout
        return;
      }
      if (!data?.id) throw new Error("Missing session id.");
      const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
      if (error) throw error;
    } catch (e) {
      console.error(e);
      setErrorMsg(e.message || "Network error: Failed to fetch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl p-6">
      <h1 className="text-2xl font-bold mb-4">Your Cart</h1>

      {errorMsg && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-red-700">
          {errorMsg}
        </div>
      )}

      {!cartItems.length ? (
        <p>
          Your cart is empty.{" "}
          <Link to="/exhaust" className="underline">
            Browse products
          </Link>
          .
        </p>
      ) : (
        <>
          <ul className="divide-y">
            {cartItems.map((item) => (
              <li
                key={`${item.id}-${item.variant || "default"}`}
                className="py-4 flex items-center gap-4"
              >
                <img
                  src={toAbsoluteUrl(item.imageUrl || item.img || item.image)}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-lg border border-neutral-200"
                  onError={(e) => {
                    e.target.src =
                      "https://placehold.co/80x80/e2e8f0/94a3b8?text=Image";
                  }}
                />
                <div className="flex-1">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm opacity-70">
                    ${Number(item.price || 0).toFixed(2)}
                  </div>
                  <div className="mt-2">
                    <label className="text-xs mr-2">Qty:</label>
                    <input
                      type="number"
                      min="1"
                      className="w-16 rounded border border-neutral-300 px-2 py-1 text-sm"
                      value={item.quantity || 1}
                      onChange={(e) => {
                        const newQty = parseInt(e.target.value, 10);
                        if (newQty > 0) {
                          onUpdateQuantity(item.id, newQty);
                        }
                      }}
                    />
                  </div>
                </div>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className="text-sm underline"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-center justify-between">
            <div className="text-lg font-semibold">Total: ${total.toFixed(2)}</div>
            <div className="flex gap-3">
              <button onClick={onClearCart} className="underline text-sm">
                Clear Cart
              </button>
              <button
                onClick={handleCheckout}
                disabled={submitting}
                className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
              >
                {submitting ? "Processing..." : "Checkout"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

